/* NIAM — mix-and-match pack builder.
 *
 * Every garment in the pack is added as its own line item with its real variant
 * id, so inventory decrements per colour and the order is itemised. Pack pricing
 * comes from an automatic discount, not from this script.
 *
 * Uniform mode: one product, a tier toggle, one size for the whole pack.
 * Variety mode: one source per slot, each slot with its own size picker.
 */
(function () {
  'use strict';

  var SEP = '::';

  function init(root) {
    if (root.dataset.nmpkReady === '1') return;
    root.dataset.nmpkReady = '1';

    var $ = function (sel) { return root.querySelector(sel); };
    var readJSON = function (sel, fallback) {
      var el = root.querySelector(sel);
      if (!el) return fallback;
      try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
    };

    var sources = readJSON('[data-nmpk-sources]', []);
    var tiers = readJSON('[data-nmpk-tiers]', []).filter(function (t) { return t.count > 0; });
    if (!sources.length || !tiers.length) return;

    var mode = root.dataset.mode || 'uniform';
    var variety = mode === 'fixed';   // slot i is bolted to source i
    var pool = mode === 'pool';       // any slot may pick any pooled source
    var moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    var afterAdd = root.dataset.afterAdd || 'cart';

    /* --------------------------------------------------------- source index */

    var csv = function (s) {
      return (s || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean);
    };

    sources.forEach(function (src) {
      src.colors = [];
      src.sizes = [];
      src.byKey = {};
      src.colorImage = {};

      src.variants.forEach(function (v) {
        var c = v.opts[src.colorIdx];
        var s = v.opts[src.sizeIdx];
        if (src.colors.indexOf(c) === -1) src.colors.push(c);
        if (src.sizes.indexOf(s) === -1) src.sizes.push(s);
        src.byKey[c + SEP + s] = v;
        if (!src.colorImage[c] && v.img) src.colorImage[c] = v.img;
      });

      // An allow-list keeps prints out of a staples pack without touching the product.
      var allow = csv(src.allow);
      if (allow.length) {
        src.colors = src.colors.filter(function (c) { return allow.indexOf(c) !== -1; });
      }

      // Merchandising order beats the product's own order: staples first.
      var order = csv(src.order);
      if (order.length) {
        src.colors.sort(function (a, b) {
          var ia = order.indexOf(a), ib = order.indexOf(b);
          if (ia === -1 && ib === -1) return 0;
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        });
      }
    });

    var stock = function (src, c, s) {
      var v = src.byKey[c + SEP + s];
      if (!v || !v.available) return 0;
      // Available but untracked reports 0; treat as plenty.
      return v.qty > 0 ? v.qty : 99;
    };

    var money = function (cents) {
      var amount = (cents / 100).toFixed(2);
      if (moneyFormat.indexOf('{{amount}}') !== -1) return moneyFormat.replace('{{amount}}', amount);
      if (moneyFormat.indexOf('{{amount_no_decimals}}') !== -1) {
        return moneyFormat.replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
      }
      return '$' + amount;
    };

    /* --------------------------------------------------------------- state */

    var blankPicks = function (n) {
      var a = [];
      for (var i = 0; i < n; i++) {
        a.push({ srcIdx: variety ? i : (pool ? null : 0), color: null, size: null });
      }
      return a;
    };

    var S = { tier: tiers[0], size: null, picks: blankPicks(tiers[0].count) };

    // Fixed variety draws slot i from source i; pool lets the slot choose;
    // uniform reuses source 0.
    var sourceFor = function (i) {
      if (variety) return sources[i];
      if (pool) {
        var k = S.picks[i] && S.picks[i].srcIdx;
        return (k === null || k === undefined) ? null : sources[k];
      }
      return sources[0];
    };

    var minPrice = sources.reduce(function (m, s) { return Math.min(m, s.price); }, Infinity);

    var fullPrice = function (tier) {
      if (variety) {
        return sources.reduce(function (sum, s) { return sum + s.price; }, 0);
      }
      if (pool) {
        // Unchosen slots price at the cheapest pooled item, so the figure only
        // ever moves up as the buyer picks. Exact once the pack is full.
        var t = 0;
        for (var i = 0; i < tier.count; i++) {
          var src = i < S.picks.length ? sourceFor(i) : null;
          t += src ? src.price : minPrice;
        }
        return t;
      }
      return sources[0].price * tier.count;
    };

    var packTotal = function (tier) {
      return Math.round(fullPrice(tier) * (1 - tier.pct / 100));
    };

    var filled = function () {
      return S.picks.filter(function (p) {
        return p.color && p.size && (!pool || (p.srcIdx !== null && p.srcIdx !== undefined));
      }).length;
    };

    /* ----------------------------------------------------------- the shots */

    function renderShots() {
      var el = $('[data-nmpk-shots]');
      if (!el) return;
      el.innerHTML = '';

      var n = S.tier.count;
      var withColour = S.picks.filter(function (p) { return p.color; }).length;

      if (withColour === 0 && S.tier.emptyImg) {
        el.classList.add('is-single');
        el.classList.add('is-hero');
        var hero = document.createElement('figure');
        hero.className = 'nmpk__shot nmpk__shot--hero';
        var hi = document.createElement('img');
        hi.src = S.tier.emptyImg;
        hi.alt = S.tier.label + ' pack';
        hi.decoding = 'async';
        hero.appendChild(hi);
        el.appendChild(hero);
        renderDots(1);
        var cap0 = $('[data-nmpk-cap]');
        if (cap0) cap0.textContent = 'Pick a colour to start building';
        return;
      }
      el.classList.remove('is-hero');
      el.classList.toggle('is-single', withColour <= 1);

      for (var i = 0; i < n; i++) {
        var pick = S.picks[i];
        // A lone shot fills the frame; empty slots would stack full-width under
        // it, so they wait until the grid exists.
        if (withColour === 1 && !pick.color) continue;

        var cell = document.createElement('figure');
        cell.className = 'nmpk__shot' + (pick.color ? '' : ' nmpk__shot--empty');

        if (pick.color) {
          var src = sourceFor(i);
          var img = document.createElement('img');
          img.src = src.colorImage[pick.color] || '';
          img.alt = pick.color + ' ' + src.title;
          img.loading = 'eager';
          img.decoding = 'async';
          cell.appendChild(img);
          var cap = document.createElement('figcaption');
          cap.textContent = variety ? src.label + ' · ' + pick.color : pick.color;
          cell.appendChild(cap);
        } else {
          var ph = document.createElement('span');
          ph.className = 'nmpk__shotNum';
          ph.textContent = String(i + 1);
          cell.appendChild(ph);
        }
        el.appendChild(cell);
      }

      renderDots(el.children.length);

      var cap2 = $('[data-nmpk-cap]');
      if (cap2) {
        cap2.textContent = filled() === n ? 'Your pack' : 'Your pack, as you build it';
      }
    }

    /* Mobile only: the grid becomes a scroll-snap carousel, so these track it. */
    function renderDots(count) {
      var dots = $('[data-nmpk-dots]');
      if (!dots) return;
      var total = typeof count === 'number' ? count : S.tier.count;
      dots.innerHTML = '';
      if (total < 2) return;
      for (var i = 0; i < total; i++) {
        var d = document.createElement('span');
        d.className = 'nmpk__dot' + (i === 0 ? ' is-on' : '');
        dots.appendChild(d);
      }
    }

    function syncDots() {
      var shots = $('[data-nmpk-shots]');
      var dots = $('[data-nmpk-dots]');
      if (!shots || !dots || !shots.children.length) return;
      var w = shots.children[0].getBoundingClientRect().width || 1;
      var idx = Math.round(shots.scrollLeft / w);
      [].forEach.call(dots.children, function (d, i) {
        d.classList.toggle('is-on', i === idx);
      });
    }

    /* ------------------------------------------------------------ controls */

    function swatchButton(src, colour, isOn, enabled, title, onPick) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'nmpk__sw';
      b.setAttribute('aria-pressed', String(isOn));
      b.setAttribute('aria-label', colour);
      var sw = (src.swatches || {})[colour] || {};
      var fill = sw.color
        ? 'background-color:' + sw.color
        : (sw.image ? 'background-image:url(' + sw.image + ')' : 'background-color:#bdb7ac');
      b.innerHTML = '<i style="' + fill + '"></i>';
      b.disabled = !enabled;
      b.title = title;
      b.addEventListener('click', onPick);
      return b;
    }

    function renderTiers() {
      var step = $('[data-nmpk-tier-step]');
      var el = $('[data-nmpk-tiers-ui]');
      if (!el || !step) return;
      // A single-tier pack has nothing to choose, so the step disappears.
      if (tiers.length < 2) { step.hidden = true; return; }
      step.hidden = false;
      el.innerHTML = '';
      tiers.forEach(function (t) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'nmpk__seg';
        b.setAttribute('aria-pressed', String(S.tier.count === t.count));
        b.innerHTML = t.label + '<span class="nmpk__segSub">' + money(packTotal(t)) + ' · save ' + t.pct + '%</span>';
        b.addEventListener('click', function () {
          if (S.tier.count === t.count) return;
          var keep = S.picks.slice(0, t.count);
          S.tier = t;
          S.picks = blankPicks(t.count);
          keep.forEach(function (p, i) { if (p) S.picks[i] = p; });
          renderAll();
        });
        el.appendChild(b);
      });
      var echo = $('[data-nmpk-tier-echo]');
      if (echo) echo.textContent = '— ' + S.tier.label;
    }

    function renderSharedSizes() {
      var el = $('[data-nmpk-sizes]');
      if (!el) return;
      var src = sources[0];
      el.innerHTML = '';
      src.sizes.forEach(function (s) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'nmpk__size';
        b.textContent = s;
        b.setAttribute('aria-pressed', String(S.size === s));
        b.disabled = !src.colors.some(function (c) { return stock(src, c, s) > 0; });
        b.addEventListener('click', function () {
          S.size = s;
          S.picks.forEach(function (p) {
            p.size = s;
            if (p.color) {
              var need = S.picks.filter(function (x) { return x.color === p.color; }).length;
              if (stock(src, p.color, s) < need) p.color = null;
            }
          });
          renderAll();
        });
        el.appendChild(b);
      });
      var echo = $('[data-nmpk-size-echo]');
      if (echo) echo.textContent = S.size ? '— ' + S.size : '';
    }

    function renderSlots() {
      var el = $('[data-nmpk-slots]');
      el.innerHTML = '';

      for (var i = 0; i < S.tier.count; i++) {
        (function (idx) {
          var src = sourceFor(idx);
          var pick = S.picks[idx];

          var wrap = document.createElement('div');
          wrap.className = 'nmpk__slot';

          var head = document.createElement('div');
          head.className = 'nmpk__slotHead';
          var nm = document.createElement('span');
          nm.className = 'nmpk__slotName';
          nm.textContent = pool
            ? 'Item ' + (idx + 1)
            : (variety ? src.label : (src.label || 'Shirt') + ' ' + (idx + 1));
          var vv = document.createElement('span');
          var done = src && pick.color && pick.size;
          vv.className = 'nmpk__slotVal' + (done ? '' : ' nmpk__slotVal--empty');
          vv.textContent = done
            ? (pool ? src.label + ' · ' : '') + pick.color + ' · ' + pick.size
            : (pool && !src ? 'Choose a style' : (variety || pool ? 'Choose a size and colour' : 'Choose a colour'));
          head.appendChild(nm);
          head.appendChild(vv);
          wrap.appendChild(head);

          // Pool mode: the slot picks WHICH garment before size or colour exist.
          if (pool) {
            var styleRow = document.createElement('div');
            styleRow.className = 'nmpk__styles';
            sources.forEach(function (s2, si) {
              var sb = document.createElement('button');
              sb.type = 'button';
              sb.className = 'nmpk__style';
              sb.setAttribute('aria-pressed', String(pick.srcIdx === si));
              sb.innerHTML = '<b>' + s2.label + '</b><span>' + money(s2.price) + '</span>';
              sb.addEventListener('click', function () {
                if (pick.srcIdx === si) return;
                pick.srcIdx = si;
                pick.size = null;
                pick.color = null;
                renderAll();
              });
              styleRow.appendChild(sb);
            });
            wrap.appendChild(styleRow);
            if (!src) { el.appendChild(wrap); return; }
          }

          if (variety || pool) {
            if (!pool) {
              var subT = document.createElement('p');
              subT.className = 'nmpk__slotProduct';
              subT.textContent = src.title + ' · ' + money(src.price);
              wrap.appendChild(subT);
            }

            var sizeRow = document.createElement('div');
            sizeRow.className = 'nmpk__sizes nmpk__slotSizes';
            src.sizes.forEach(function (s) {
              var b = document.createElement('button');
              b.type = 'button';
              b.className = 'nmpk__size';
              b.textContent = s;
              b.setAttribute('aria-pressed', String(pick.size === s));
              b.disabled = !src.colors.some(function (c) { return stock(src, c, s) > 0; });
              b.addEventListener('click', function () {
                pick.size = s;
                if (pick.color && stock(src, pick.color, s) < 1) pick.color = null;
                renderAll();
              });
              sizeRow.appendChild(b);
            });
            wrap.appendChild(sizeRow);
          }

          var row = document.createElement('div');
          row.className = 'nmpk__swatches';
          src.colors.forEach(function (c) {
            var enabled = !!pick.size;
            if (enabled) {
              // Uniform packs can repeat a colour, so count what the other slots took.
              var used = (variety || pool) ? 0 : S.picks.filter(function (p, j) {
                return j !== idx && p.color === c;
              }).length;
              enabled = stock(src, c, pick.size) >= used + 1;
            }
            var title = !pick.size
              ? c + ' — pick a size first'
              : (!enabled ? c + ' — not enough stock in ' + pick.size : c);
            row.appendChild(swatchButton(src, c, pick.color === c, enabled, title, function () {
              pick.color = pick.color === c ? null : c;
              renderAll();
            }));
          });
          wrap.appendChild(row);
          el.appendChild(wrap);
        })(i);
      }
    }

    function renderPrice() {
      var full = fullPrice(S.tier);
      var now = packTotal(S.tier);
      $('[data-nmpk-price]').textContent = money(now);
      $('[data-nmpk-was]').textContent = money(full);
      $('[data-nmpk-save]').textContent = 'Save ' + S.tier.pct + '% · ' + money(full - now);
    }

    function renderDock() {
      var n = S.tier.count;
      var got = filled();
      var ready = got === n;
      $('[data-nmpk-prog-count]').textContent = got + ' of ' + n;
      var bar = $('[data-nmpk-bar]');
      bar.style.width = (got / n) * 100 + '%';
      bar.classList.toggle('is-done', ready);
      $('[data-nmpk-prog-label]').textContent = ready
        ? 'Pack complete'
        : (mode === 'uniform' && !S.size ? 'Pick a size to start' : 'Choose ' + (n - got) + ' more');
      var cta = $('[data-nmpk-add]');
      cta.disabled = !ready;
      cta.textContent = ready
        ? 'Add ' + S.tier.label + ' — ' + money(packTotal(S.tier))
        : (cta.dataset.idle || 'Select your options');
    }

    // Steps renumber themselves when the tier step is hidden.
    function renderStepNumbers() {
      var single = tiers.length < 2;
      var colourNo = $('[data-nmpk-no-colour]');
      var buyNo = $('[data-nmpk-no-buy]');
      var n;
      if (variety) {
        n = 1;
      } else if (pool) {
        n = single ? 1 : 2;
      } else {
        var sizeNo = $('[data-nmpk-no-size]');
        if (sizeNo) sizeNo.textContent = single ? '1' : '2';
        n = single ? 2 : 3;
      }
      if (colourNo) colourNo.textContent = String(n);
      if (buyNo) buyNo.textContent = String(n + 1);
    }

    function renderAll() {
      renderTiers();
      if (mode === 'uniform') renderSharedSizes();
      renderSlots();
      renderStepNumbers();
      renderPrice();
      renderDock();
      renderShots();
    }

    /* --------------------------------------------------------- add to cart */

    var cta = $('[data-nmpk-add]');
    cta.dataset.idle = cta.textContent.trim();
    var err = $('[data-nmpk-error]');

    cta.addEventListener('click', function () {
      if (cta.disabled) return;
      err.hidden = true;

      var items = S.picks.map(function (p, i) {
        var src = sourceFor(i);
        return {
          id: src.byKey[p.color + SEP + p.size].id,
          quantity: 1,
          properties: { _pack: S.tier.label }
        };
      });

      cta.setAttribute('aria-busy', 'true');
      cta.textContent = 'Adding…';

      var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      fetch(routeRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) throw new Error(data.description || data.message || 'That pack could not be added.');
            return data;
          });
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (afterAdd === 'stay') {
            cta.removeAttribute('aria-busy');
            cta.textContent = 'Added to cart';
            return;
          }
          if (afterAdd === 'drawer') {
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.open === 'function') {
              drawer.open();
              cta.removeAttribute('aria-busy');
              renderDock();
              return;
            }
          }
          window.location.href = routeRoot + 'cart';
        })
        .catch(function (e) {
          cta.removeAttribute('aria-busy');
          renderDock();
          err.hidden = false;
          err.textContent = e.message;
        });
    });

    // Warm the colour shots so swapping never flashes an empty box.
    sources.forEach(function (src) {
      Object.keys(src.colorImage).forEach(function (c) {
        var pre = new Image();
        pre.decoding = 'async';
        pre.src = src.colorImage[c];
      });
    });

    var shotsEl = $('[data-nmpk-shots]');
    if (shotsEl) shotsEl.addEventListener('scroll', syncDots, { passive: true });

    renderAll();
  }

  function boot() {
    [].forEach.call(document.querySelectorAll('[data-nmpk]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
